import * as React from 'react';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import ResourcePanel from '../ResourcePanel';
import YoutubeLinkPanel from '../YoutubeLinkPanel';
import { updateEditableMedia } from '../../../../containers/Editor/actions';
import { getYoutubeLink } from '../../../../containers/Editor/utils/helpers';
import { EditableType } from '../../../../containers/Editor/constants';
import { getOptimizedImage, getImageCenteredPositionAndSize } from '../../../../containers/Editor/utils/helpers';
import { FileType } from '../../../../containers/ImportManager/actions';
import Utils from '../../../../utils';
import { Api } from '../../../../typings/api';
import DriveItemUtils from '../../../../utils/driveItem';
import { I18n } from 'react-redux-i18n';
import { InputWrapper, Label } from '../styles';

interface MediaPanelProps {
	type: EditableType;
	id: string;
	page: number;
	label: string;
	defaultLink: string;
	driveLink: string;
	externalLink: string;
	isExternalLinkActive: boolean;
	frameWidth: number;
	frameHeight: number;
	importManagerId: string;
	isImporting: boolean;
	dispatch: Dispatch<StoreState>;
	isPercentageValues: boolean;
	isAccessibleVersion: boolean;
	altAttr: string | undefined;
	setAltAttr:  React.Dispatch<React.SetStateAction<string | undefined>>;
	src: string | undefined;
	setSrc: React.Dispatch<React.SetStateAction<string | undefined>>;
}

interface ConfigItem {
	iconName: string;
	defaultLabel: string;
	isValidResourceFromApp: (item: Api.DriveItem) => boolean;
	fileExtensions: string[];
	canDragAndResize: boolean;
	fileType?: FileType;
	linkPanel?: React.ReactNode;
	isOptimizedImageImport?: boolean;
}

const IMAGE_MAX_WIDTH = 1920;
const SKIP_OPTIMIZATION_IMAGE_FORMAT = 'png';

const MediaPanel = ({
						type,
						id,
						page,
						label,
						defaultLink,
						driveLink,
						externalLink,
						isExternalLinkActive,
						frameWidth,
						frameHeight,
						importManagerId,
						isImporting,
						dispatch,
						isPercentageValues,
						isAccessibleVersion,
						altAttr,
						setAltAttr,
						src,
						setSrc,
					}: MediaPanelProps) => {
	const altAttrInputElement = React.useRef<HTMLInputElement>(null);
	const srcInputElement = React.useRef<HTMLInputElement>(null);

	const config: { [k: string]: ConfigItem } = {
		[EditableType.image]: {
			iconName: 'iconset_uprez_image',
			defaultLabel: 'messages.default_image',
			isValidResourceFromApp: ({ type }) => type === Api.DriveItemType.Image,
			fileExtensions: DriveItemUtils.imageWithPdfExtensions,
			canDragAndResize: true,
			isOptimizedImageImport: true,
		},
		[EditableType.video]: {
			iconName: 'iconset_uprez_video',
			defaultLabel: 'messages.default_video',
			isValidResourceFromApp: ({ type }) => type === Api.DriveItemType.Movie,
			fileExtensions: DriveItemUtils.videoExtensions,
			canDragAndResize: false,
			linkPanel: (
				<YoutubeLinkPanel
					value={externalLink}
					onChange={(value) => {
						dispatch(updateEditableMedia(page, id, {
							externalLink: value,
							isExternalLinkActive: true,
							canDragAndResize: false,
						}));
					}}
				/>
			),
		},
		[EditableType.animation]: {
			iconName: 'iconset_uprez_animation',
			defaultLabel: 'messages.default_animation',
			isValidResourceFromApp: ({ type }) => type === Api.DriveItemType.Component,
			fileExtensions: DriveItemUtils.componentExtensions,
			canDragAndResize: false,
			fileType: FileType.COMPONENT,
		},
	};

	const {
		iconName,
		defaultLabel,
		isValidResourceFromApp,
		fileExtensions,
		canDragAndResize,
		linkPanel,
		fileType,
		isOptimizedImageImport,
	} = config[type];

	const handleResourceSelect = (driveItem: Api.DriveItem): void => {
		const { src, label, name, resolution } = driveItem;
		let newDriveLink = src;
		let width = frameWidth;
		let height = frameHeight;

		if (resolution) {
			width = +resolution.split('x')[0];
			height = +resolution.split('x')[1];
		}

		const canOptimizeImage = type === EditableType.image &&
			width > IMAGE_MAX_WIDTH &&
			Utils.getFileExtension(name) !== SKIP_OPTIMIZATION_IMAGE_FORMAT;

		if (canOptimizeImage) {
			const optimizedImage = getOptimizedImage(driveItem, width, height, IMAGE_MAX_WIDTH);
			width = optimizedImage.width;
			height = optimizedImage.height;
			newDriveLink = optimizedImage.src;
		}

		const isSameImage = type === EditableType.image && driveLink === newDriveLink;
		if (isSameImage) {
			// we have to add some hash to url in order to make browser to load the same file
			newDriveLink += `?${Math.random()}&`;
		}

		if (type === EditableType.animation) {
			newDriveLink += '/index.html';
		}

		dispatch(updateEditableMedia(page, id, {
			...getImageCenteredPositionAndSize(frameWidth, frameHeight, width, height, isPercentageValues),
			driveLink: newDriveLink,
			label,
			isExternalLinkActive: false,
			isLoaded: false,
			canDragAndResize,
		}));
	};

	const handleResourceReset = (): void => {
		dispatch(updateEditableMedia(page, id, {
			label: '',
			driveLink: '',
			externalLink: getYoutubeLink(defaultLink) ? defaultLink : '',
			isExternalLinkActive: !!getYoutubeLink(defaultLink),
			canDragAndResize: false,
		}));
	};

	const isResetButtonDisplayed = isExternalLinkActive
		? !!externalLink && externalLink !== defaultLink
		: !!driveLink;

	const handleAltAttrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setAltAttr(e.target.value);
	};

	const handleImageSrcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSrc(e.target.value);
	};

	return (isAccessibleVersion
			? (
				<>
					<Label value="messages.alt_attribute" />
					<InputWrapper
						hasButtonRemove={false}
						hasLabel={true}
					>
						<input
							ref={altAttrInputElement}
							type="text"
							id={id}
							value={altAttr}
							placeholder={I18n.t('messages.enter_image_description')}
							onChange={handleAltAttrChange}
							autoFocus={true}
						/>
					</InputWrapper>
					<Label value="messages.image_source" />
					<InputWrapper
						hasButtonRemove={false}
						hasLabel={true}
					>
						<input
							ref={srcInputElement}
							type="text"
							id={`${id}-src`}
							value={src}
							placeholder={I18n.t('messages.enter_image_source')}
							onChange={handleImageSrcChange}
						/>
					</InputWrapper>
				</>
			)
			: (
				<ResourcePanel
					iconName={iconName}
					label={driveLink ? label : defaultLabel}
					isExternalLinkActive={isExternalLinkActive}
					linkPanel={linkPanel}
					importManagerId={importManagerId}
					isImporting={isImporting}
					isResetButtonDisplayed={isResetButtonDisplayed}
					isValidResourceFromApp={isValidResourceFromApp}
					fileExtensions={fileExtensions}
					isFileExtensionsDisplayed={true}
					isOptimizedImageImport={isOptimizedImageImport}
					fileType={fileType}
					dispatch={dispatch}
					onResourceSelect={handleResourceSelect}
					onResourceReset={handleResourceReset}
				/>
			)
	);
};

export default MediaPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/MediaPanel/index.tsx